import "./kept-projects.css";

import type { DirectoryHandle } from "idfweb-project";
import type { PageToRuntime, SavedReply } from "idfweb-runtime-protocol";
import type { Reporter } from "idfweb-tasks";

import { allowedAccess } from "./local-directory.ts";
import { holdProject, type ProjectLock } from "./project-lock.ts";
import {
    deleteProjectRecord,
    folderRecord,
    projectRecord,
    projectRecords,
    putProjectRecord,
    type ProjectRecord,
} from "./project-records.ts";
import { forgetProjectState, setStateProject } from "./project-state.ts";
import { newProjectId, pagePath } from "./project-url.ts";
import { browserStorageSupported, savedTree, savedTreeBytes, storageRefused, storageRoom } from "./saved-tree.ts";
import { askAboutOtherTab } from "./sync-dialogs.ts";
import type { WelcomeItem, WelcomePage } from "./welcome.ts";

/**
 * The page's side of keeping projects: every project opened is kept, in the
 * browser or in the folder it came from, under an id that is in the page's
 * URL, so that a reload opens it again. See "Kept Projects" in the page's
 * README. The records are in `project-records.ts`; the worker keeps the
 * files in step.
 */
export interface KeptProjects {
    /** The project open in this tab, once it is kept. */
    current(): ProjectRecord | undefined;
    /** An example is being opened: keep it in the browser once it is open, with the example link it came from. */
    openingNew(source?: string): void;
    /** The copy of the example at `source` opened last, if this browser keeps one. */
    copyOf(source: string): Promise<ProjectRecord | undefined>;
    /** Open a kept project from a click, asking first when another tab has it. */
    openKept(record: ProjectRecord): Promise<boolean>;
    /** Open a folder the user chose, kept in sync with it or copied into the browser. */
    openFolder(handle: DirectoryHandle, sync: boolean): Promise<void>;
    /**
     * Open the project `id` from the page's URL. Resolves to false when it is
     * not in this browser, or when the user left it closed.
     */
    reopen(id: string): Promise<boolean>;
    /** The worker opened a project, or could not. */
    opened(name: string, error: string | undefined): void;
    /** The welcome window's page of kept projects, when there are any. */
    welcomeItems(): Promise<WelcomeItem[]>;
    /**
     * Keep the open project, now in the browser, in a new folder in `parent`
     * named as its tree, and remove the browser's copy. Resolves to why not.
     */
    moveToFolder(parent: DirectoryHandle): Promise<string | undefined>;
    /** Keep the open project, now in a folder, in the browser; the folder stays as it is. Resolves to why not. */
    moveToBrowser(): Promise<string | undefined>;
    receive(message: SavedReply): void;
}

export function createKeptProjects(options: {
    post(message: PageToRuntime): void;
    reporter: Reporter;
    /** The configuration the page was opened with, which its URLs keep. */
    configuration: string | undefined;
    /** Show the welcome window at `page`: a folder needs a click before it is read again. */
    showPage(page: WelcomePage): void;
    /** The project in this tab is now `record`, or no longer kept. */
    changed(record: ProjectRecord | undefined): void;
    /** Another tab took the project, after `changed(undefined)`: let go of what that tab reconnects. */
    taken(): void;
}): KeptProjects {
    const { post, reporter } = options;
    const replies = new Map<number, (reply: SavedReply) => void>();
    let nextId = 1;
    const ask = (message: (id: number) => PageToRuntime): Promise<SavedReply> => {
        const id = nextId++;
        return new Promise((resolve) => {
            replies.set(id, resolve);
            post(message(id));
        });
    };

    let current: ProjectRecord | undefined;
    let lock: ProjectLock | undefined;
    /** What the open under way becomes once the worker answers. */
    let pending:
        | { kind: "new"; name?: string; source?: string }
        | { kind: "folder"; handle: DirectoryHandle }
        | { kind: "record"; record: ProjectRecord }
        | undefined;
    let refusalReported = false;

    const setUrl = (id: string | undefined) => {
        history.replaceState(history.state, "", new URL(pagePath(options.configuration, id), document.baseURI));
    };

    /** The project in this tab is no longer kept; the guest keeps its files. `keep` is a lock not to release. */
    const letGo = (keep?: ProjectLock) => {
        if (lock !== keep) lock?.release();
        lock = undefined;
        current = undefined;
        setStateProject(undefined);
        setUrl(undefined);
    };

    /** Another tab opened the project with "Open Here". */
    const lost = (record: ProjectRecord) => () => {
        if (current?.id !== record.id) return;
        lock = undefined;
        post({ type: "release" });
        letGo();
        options.changed(undefined);
        options.taken();
        reporter.message("warn", `${record.name} is open in another tab now. Changes made here are no longer saved.`);
    };

    const take = async (record: ProjectRecord, steal: boolean): Promise<ProjectLock | undefined> =>
        holdProject(record.id, steal, lost(record));

    const become = (record: ProjectRecord, held: ProjectLock | undefined) => {
        if (lock !== held) lock?.release();
        lock = held;
        current = record;
        setStateProject(record.id);
        setUrl(record.id);
        void putProjectRecord({ ...record, opened: Date.now() }).catch((e: unknown) =>
            reporter.message("error", `Could not keep ${record.name}: ${briefly(e)}`),
        );
        options.changed(record);
    };

    /** Start opening `record`, having its lock; a folder is read only with the user's permission. */
    const openRecord = async (record: ProjectRecord, held: ProjectLock | undefined): Promise<boolean> => {
        if (record.kind === "folder") {
            if (!(await allowedAccess(record.handle!, true))) {
                held?.release();
                reporter.message("warn", `idfweb may not change ${record.name}.`);
                return false;
            }
        }
        letGo(held);
        lock = held;
        setStateProject(record.id);
        pending = { kind: "record", record };
        if (record.kind === "browser")
            post({ type: "open-saved", key: record.id, name: record.name, tree: record.tree });
        else post({ type: "open-directory", directory: record.handle!, sync: true });
        return true;
    };

    /** Open `record` as a new project in the browser, copied from where it is kept. */
    const openCopy = async (record: ProjectRecord): Promise<boolean> => {
        let directory: DirectoryHandle | undefined;
        try {
            if (record.kind === "browser") directory = await savedTree(record.id, record.tree, false);
            else if (await allowedAccess(record.handle!, false)) directory = record.handle;
        } catch (e) {
            reporter.message("error", `Could not copy ${record.name}: ${briefly(e)}`);
            return false;
        }
        if (directory === undefined) {
            reporter.message("error", `Could not copy ${record.name}: its files cannot be read.`);
            return false;
        }
        letGo();
        pending = { kind: "new", name: `${record.name} copy` };
        post({ type: "open-directory", directory, sync: false });
        return true;
    };

    /** Open `record` from a click or from the URL, asking first when another tab has it. */
    const open = async (record: ProjectRecord): Promise<boolean> => {
        if (current?.id === record.id) return true;
        let held = await take(record, false);
        if (held === undefined) {
            const answer = await askAboutOtherTab(record.name);
            if (answer === "copy") return openCopy(record);
            if (answer === undefined) return false;
            held = await take(record, true);
        }
        return openRecord(record, held);
    };

    /** Keep the project the worker just opened in the browser, under a new id. */
    const keepInBrowser = async (name: string, source: string | undefined) => {
        if (!browserStorageSupported()) return;
        const refused = await storageRefused();
        if (refused !== undefined) {
            if (!refusalReported) {
                reporter.message(
                    "info",
                    `This browser window keeps no files for the site, so ${name} is lost on a reload: ${refused}`,
                );
            }
            refusalReported = true;
            return;
        }
        const id = newProjectId(name);
        askToPersist();
        const { error, tree } = await ask((reply) => ({ type: "save", id: reply, key: id, name }));
        if (error !== undefined) {
            reporter.message("error", `${name} is not kept in this browser: ${error}`);
            return;
        }
        const record: ProjectRecord = { id, name, kind: "browser", tree: tree!, opened: Date.now() };
        if (source !== undefined) record.source = source;
        become(record, await take(record, false));
    };

    const keepFolder = async (handle: DirectoryHandle) => {
        const record: ProjectRecord = {
            id: newProjectId(handle.name),
            name: handle.name,
            kind: "folder",
            tree: handle.name,
            handle,
            opened: Date.now(),
        };
        become(record, await take(record, false));
    };

    /** The open project is kept at the place `record` says now. */
    const moved = async (record: ProjectRecord): Promise<string | undefined> => {
        try {
            await putProjectRecord(record);
        } catch (e) {
            return briefly(e);
        }
        if (current?.id === record.id) {
            current = record;
            options.changed(record);
        }
        return undefined;
    };

    const moveToFolder = async (parent: DirectoryHandle): Promise<string | undefined> => {
        const record = current;
        if (record?.kind !== "browser") return "the project is not in browser storage";
        const found = await emptyFolder(parent, record.tree);
        if (typeof found === "string") return found;
        const { error } = await ask((id) => ({ type: "move", id, name: record.name, directory: found.folder }));
        if (error !== undefined) {
            if (found.made) await parent.removeEntry(record.tree, { recursive: true }).catch(() => {});
            return error;
        }
        const failed = await moved({ ...record, kind: "folder", handle: found.folder });
        if (failed !== undefined) return failed;
        const { error: left } = await ask((id) => ({ type: "delete-saved", id, key: record.id }));
        if (left !== undefined)
            reporter.message("warn", `Could not delete the browser copy of ${record.name}: ${left}`);
        return undefined;
    };

    const moveToBrowser = async (): Promise<string | undefined> => {
        const record = current;
        if (record?.kind !== "folder") return "the project is not in a local folder";
        if (!browserStorageSupported()) return "browser storage is not available";
        const refused = await storageRefused();
        if (refused !== undefined) return refused;
        askToPersist();
        const { error, tree } = await ask((id) => ({ type: "move", id, name: record.name, key: record.id }));
        if (error !== undefined) return error;
        const { handle: _, ...rest } = record;
        return moved({ ...rest, kind: "browser", tree: tree! });
    };

    const remove = async (record: ProjectRecord): Promise<string | undefined> => {
        const open = current?.id === record.id;
        const held = open ? undefined : await take(record, false);
        if (!open && held === undefined) return `${record.name} is open in another tab.`;
        try {
            if (record.kind === "browser") {
                const { error } = await ask((id) => ({ type: "delete-saved", id, key: record.id }));
                if (error !== undefined) return error;
            } else if (open) {
                post({ type: "release" });
            }
            await deleteProjectRecord(record.id);
        } catch (e) {
            return briefly(e);
        } finally {
            held?.release();
        }
        forgetProjectState(record.id);
        if (open) {
            letGo();
            options.changed(undefined);
        }
        return undefined;
    };

    const rename = async (record: ProjectRecord, name: string): Promise<string | undefined> => {
        if (name === "") return "Give the project a name.";
        try {
            const stored = (await projectRecord(record.id)) ?? record;
            await putProjectRecord({ ...stored, name });
        } catch (e) {
            return briefly(e);
        }
        if (current?.id === record.id) {
            current = { ...current, name };
            options.changed(current);
        }
        return undefined;
    };

    const listPage: WelcomePage = {
        heading: "Recent projects",
        render: (close) => renderList(close),
    };

    const renderList = (close: () => void): HTMLElement => {
        const list = document.createElement("div");
        list.className = "idfweb-welcome-list idfweb-saved";
        const fill = async () => {
            const records = await projectRecords();
            const sizes = records.map((record) =>
                record.kind === "browser"
                    ? savedTreeBytes(record.id).catch(() => undefined)
                    : Promise.resolve(undefined),
            );
            list.replaceChildren(...records.map((record, i) => row(record, sizes[i]!, close, fill)));
            if (records.length === 0) {
                list.append(note("No projects are kept in this browser."));
                return;
            }
            if (!records.some((record) => record.kind === "browser")) return;
            const summary = note("");
            summary.classList.add("idfweb-saved-summary");
            list.append(summary);
            const [known, room] = await Promise.all([Promise.all(sizes), storageRoom().catch(() => undefined)]);
            const used = known.reduce<number>((total, bytes) => total + (bytes ?? 0), 0);
            summary.textContent =
                room === undefined
                    ? `${formatBytes(used)} used in this browser`
                    : `${formatBytes(used)} used in this browser / about ${formatBytes(Math.max(room, 0))} available`;
        };
        void fill();
        return list;
    };

    /** One project: a button that opens it, and its rename and delete buttons. */
    const row = (
        record: ProjectRecord,
        bytes: Promise<number | undefined>,
        close: () => void,
        refresh: () => Promise<void>,
    ): HTMLElement => {
        const element = document.createElement("div");
        element.className = "idfweb-saved-row";
        const entry = document.createElement("button");
        entry.type = "button";
        entry.className = "idfweb-welcome-entry";
        const title = document.createElement("span");
        title.className = "title";
        title.textContent = record.name;
        const detail = document.createElement("span");
        detail.className = "detail";
        const where = record.kind === "browser" ? "In this browser" : "Folder on this computer";
        const opened = `opened ${new Date(record.opened).toLocaleString()}`;
        detail.textContent = `${where} · ${opened}`;
        void bytes.then((size) => {
            if (size !== undefined) detail.textContent = `${where} · ${formatBytes(size)} · ${opened}`;
        });
        entry.append(title, detail);
        entry.addEventListener("click", () => {
            close();
            void open(record);
        });
        const renameButton = action("edit", `Rename ${record.name}`, () => {
            const input = document.createElement("input");
            input.type = "text";
            input.className = "idfweb-saved-name";
            input.value = record.name;
            input.setAttribute("aria-label", `New name for ${record.name}`);
            const problem = note("");
            element.replaceChildren(input, problem);
            input.focus();
            input.select();
            input.addEventListener("keydown", (event) => {
                if (event.key === "Escape") {
                    // Leaves the window open: Escape only ends the rename.
                    event.preventDefault();
                    void refresh();
                } else if (event.key === "Enter") {
                    void rename(record, input.value.trim()).then((why) => {
                        if (why === undefined) void refresh();
                        else {
                            problem.textContent = why;
                            problem.classList.add("failed");
                        }
                    });
                }
            });
        });
        const question =
            record.kind === "browser"
                ? `Delete ${record.name} from this browser?`
                : `Remove ${record.name} from the list? The folder stays as it is.`;
        const deleteButton = action("trash", `Delete ${record.name}`, () => {
            const ask = note(question);
            const yes = document.createElement("button");
            yes.type = "button";
            yes.className = "idfweb-sync-button primary";
            yes.textContent = record.kind === "browser" ? "Delete" : "Remove";
            yes.addEventListener(
                "click",
                () =>
                    void remove(record).then((why) => {
                        if (why !== undefined) reporter.message("error", `Could not delete ${record.name}: ${why}`);
                        return refresh();
                    }),
            );
            const no = document.createElement("button");
            no.type = "button";
            no.className = "idfweb-sync-button";
            no.textContent = "Keep";
            no.addEventListener("click", () => void refresh());
            element.replaceChildren(ask, no, yes);
            no.focus();
        });
        element.append(entry, renameButton, deleteButton);
        return element;
    };

    /** The welcome window's page that asks for the click a folder's permission needs. */
    const reopenPage = (record: ProjectRecord): WelcomePage => ({
        heading: `Reopen ${record.name}`,
        render: (close, list) => {
            const page = document.createElement("div");
            page.className = "idfweb-welcome-list";
            const text = note(
                `${record.name} is a folder on this computer. The browser asks again before idfweb reads and changes it.`,
            );
            const reopen = document.createElement("button");
            reopen.type = "button";
            reopen.className = "idfweb-welcome-entry";
            reopen.textContent = `Reopen ${record.name}`;
            reopen.addEventListener("click", () => {
                close();
                void open(record);
            });
            const other = document.createElement("button");
            other.type = "button";
            other.className = "idfweb-welcome-entry";
            other.textContent = "Open another project";
            other.addEventListener("click", list);
            page.append(text, reopen, other);
            return page;
        },
    });

    return {
        current: () => current,
        openingNew: (source) => {
            letGo();
            pending = source === undefined ? { kind: "new" } : { kind: "new", source };
        },
        copyOf: async (source) => (await projectRecords()).find((record) => record.source === source),
        openKept: open,
        openFolder: async (handle, sync) => {
            if (!sync) {
                letGo();
                pending = { kind: "new" };
                post({ type: "open-directory", directory: handle, sync: false });
                return;
            }
            const known = await folderRecord(handle);
            if (known !== undefined) {
                // Chosen again from the picker: this tab takes it over.
                const held = known.id === current?.id ? lock : await take(known, true);
                await openRecord({ ...known, handle }, held);
                return;
            }
            letGo();
            pending = { kind: "folder", handle };
            post({ type: "open-directory", directory: handle, sync: true });
        },
        reopen: async (id) => {
            const record = await projectRecord(id);
            if (record === undefined) {
                reporter.message("warn", "The project in this page's address is not kept in this browser.");
                setUrl(undefined);
                return false;
            }
            if (
                record.kind === "folder" &&
                (await record.handle!.queryPermission?.({ mode: "readwrite" })) !== "granted"
            ) {
                options.showPage(reopenPage(record));
                return true;
            }
            return open(record);
        },
        opened: (name, error) => {
            const was = pending;
            pending = undefined;
            if (error !== undefined) {
                if (was?.kind === "record") letGo();
                return;
            }
            if (was?.kind === "new") void keepInBrowser(was.name ?? name.split("/").pop()!, was.source);
            else if (was?.kind === "folder") void keepFolder(was.handle);
            else if (was?.kind === "record") become(was.record, lock);
        },
        welcomeItems: async () => ((await projectRecords()).length > 0 ? [listPage] : []),
        moveToFolder,
        moveToBrowser,
        receive: (message) => {
            replies.get(message.id)?.(message);
            replies.delete(message.id);
        },
    };
}

function action(icon: string, title: string, run: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `idfweb-saved-action codicon codicon-${icon}`;
    button.title = title;
    button.setAttribute("aria-label", title);
    button.addEventListener("click", run);
    return button;
}

/** Ask the browser to keep the site's storage, before the first write, so that it does not evict projects to make room. */
function askToPersist(): void {
    void Promise.resolve()
        .then(() => navigator.storage.persist?.())
        .catch(() => false);
}

/**
 * The folder `name` in `parent`, made when it is not there, or why it cannot
 * take a project: a folder with files in it is never written into.
 */
async function emptyFolder(
    parent: DirectoryHandle,
    name: string,
): Promise<{ folder: DirectoryHandle; made: boolean } | string> {
    try {
        let folder: DirectoryHandle;
        try {
            folder = await parent.getDirectoryHandle(name);
        } catch (e) {
            if (!(e instanceof DOMException && e.name === "NotFoundError")) throw e;
            return { folder: await parent.getDirectoryHandle(name, { create: true }), made: true };
        }
        for await (const _ of folder.entries()) return `the folder ${parent.name}/${name} is not empty`;
        return { folder, made: false };
    } catch (e) {
        if (e instanceof DOMException && e.name === "TypeMismatchError")
            return `a file named ${name} already exists in ${parent.name}`;
        return briefly(e);
    }
}

/** A size as people read it: "44 kB", "1.2 MB". */
export function formatBytes(bytes: number): string {
    if (bytes < 1000) return `${bytes} B`;
    const units = ["kB", "MB", "GB", "TB"];
    let value = bytes / 1000;
    let unit = 0;
    for (; value >= 1000 && unit < units.length - 1; unit++) value /= 1000;
    return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

function note(text: string): HTMLParagraphElement {
    const p = document.createElement("p");
    p.className = "idfweb-welcome-note";
    p.textContent = text;
    return p;
}

function briefly(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
}
